import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { Button } from "../../components/ui/Button";
import { cn } from "../../components/ui/cn";
import { Panel } from "../../components/ui/Panel";
import type {
  Team,
  User } from "../../types";
import { slugify } from "./shared";

export function TeamSettings({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");

  const teams = useQuery({ queryKey: queryKeys.teams, queryFn: api.teams });
  const users = useQuery({ queryKey: queryKeys.users, queryFn: api.users });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.teams });

  const create = useMutation({
    mutationFn: (teamName: string) =>
      api.createTeam({ name: teamName, slug: slugify(teamName) }),
    onSuccess: () => {
      setName("");
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteTeam(id),
    onSuccess: invalidate,
  });

  const setMembers = useMutation({
    mutationFn: ({ id, userIds }: { id: number; userIds: number[] }) =>
      api.setTeamMembers(id, userIds),
    onSuccess: invalidate,
  });

  return (
    <Panel
      collapsible
      title="Teams"
    >
      {isAdmin ? (
        <form
          className="mb-4 flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim()) create.mutate(name.trim());
          }}
        >
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="New team name"
            className={cn(
              "h-9 flex-1 rounded-md border border-line bg-surface px-2.5 text-sm text-content",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
            )}
          />
          <Button type="submit" disabled={!name.trim() || create.isPending}>
            Add team
          </Button>
        </form>
      ) : null}

      {(teams.data ?? []).length === 0 ? (
        <p className="text-sm text-muted">
          No teams yet. Without one, “My team” shows nothing.
        </p>
      ) : (
        <div className="space-y-4">
          {(teams.data ?? []).map((team) => (
            <TeamRow
              key={team.id}
              team={team}
              users={users.data ?? []}
              isAdmin={isAdmin}
              onRemove={() => remove.mutate(team.id)}
              onMembersChange={(userIds) =>
                setMembers.mutate({ id: team.id, userIds })
              }
            />
          ))}
        </div>
      )}
    </Panel>
  );
}

function TeamRow({
  team,
  users,
  isAdmin,
  onRemove,
  onMembersChange,
}: {
  team: Team;
  users: User[];
  isAdmin: boolean;
  onRemove: () => void;
  onMembersChange: (userIds: number[]) => void;
}) {
  const memberIds = new Set(team.members.map((member) => member.id));

  return (
    <div className="rounded-md border border-line p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-sm font-medium text-content">{team.name}</span>
        <span className="text-xs text-muted">
          {team.members.length} member{team.members.length === 1 ? "" : "s"}
        </span>
        {isAdmin ? (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={onRemove}
          >
            Remove
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {users.map((user) => {
          const active = memberIds.has(user.id);
          return (
            <button
              key={user.id}
              type="button"
              disabled={!isAdmin}
              onClick={() =>
                onMembersChange(
                  active
                    ? [...memberIds].filter((id) => id !== user.id)
                    : [...memberIds, user.id],
                )
              }
              className={cn(
                "rounded-full px-2 py-0.5 text-xs transition-colors disabled:opacity-60",
                active
                  ? "bg-brand-600 text-white"
                  : "bg-surface-2 text-muted-on-tint hover:text-brand-600 dark:hover:text-brand-400",
              )}
            >
              {user.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

